<template>
  <div class="panel-container">
    <!-- 顶部搜索栏 -->
    <div class="top-container">
      <el-form class="power-form" inline :model="queryForm">
        <div class="form-left-items">
          <el-form-item :label="$str.getKey('数据分析/日期')">
            <el-date-picker v-model="queryForm.timer" @change="timeRangeChange" :clearable="false"
              :disabled-date="disabledDate" type="daterange" format="YYYY-MM-DD" size="default"
              class="filter-date-picker"></el-date-picker>
          </el-form-item>
          <el-form-item :label="$str.getKey('数据分析/电表')">
            <el-select v-model="queryForm.currMeter" :placeholder="$str.getKey('数据分析/请选择电表')"
              @change="selectedMeterChange" size="default" class="filter-select-input">
              <el-option v-for="item in queryForm.meterList" :label="item.label" :value="item.value"
                :key="item.value"></el-option>
            </el-select>
          </el-form-item>
        </div>
        <el-form-item>
          <div class="filter-actions">
            <v-btn color="#2655f1" rounded="xl" class="action-btn" @click="onSearch">
              <v-icon start icon="mdi-magnify" />{{ $str.getKey('数据分析/查询') }}
            </v-btn>
            <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onReset">
              <v-icon start icon="mdi-refresh" />{{ $str.getKey('数据分析/重置') }}
            </v-btn>
          </div>
        </el-form-item>
      </el-form>
    </div>
    <div class="main-container">
      <!-- 电表数据分析图表 -->
      <div ref="linesChartDom" class="chart-container"></div>
      <!-- 加载状态 -->
      <div v-if="chartLoading" class="chart-status">
        <div class="status-content">
          <span class="loading-text">{{ $str.getKey('数据分析/加载中...') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, reactive, nextTick, onMounted, onBeforeUnmount } from "vue";
import dayjs from "dayjs";
import * as echarts from 'echarts';
import { cloneDeep } from "@/utils";
import { meterApi } from "@/api/modules/device/index.js";
import { useAppStore } from "@/store/modules/app";
import { queryMeterAnalysisData } from "./api/index.js";
import { getChartOption } from "./linesChartOptions.js";

const $str = window.$str;

/** 注册局部语言词条，返回全局的词条翻译方法 */
// const registWordItems = inject('registWordItems');
// const wordItems = [
//   ['请选择电表', {
//     zh: '请选择电表',
//     en: 'Please select meter',
//   }],
//   ['有功功率', {
//     zh: '有功功率',
//     en: 'ActivePower',
//   }],
//   ['无功功率', {
//     zh: '无功功率',
//     en: 'ReactivePower',
//   }],
//   ['功率因数', {
//     zh: '功率因数',
//     en: 'PowerFactor',
//   }],
// ]
// const $lt = registWordItems(import.meta.url, wordItems) // 未使用，已注释


const debounceTimer = ref(); // 查询防抖
const chartLoading = ref(false); // 图表加载状态
const chartEmpty = ref(false); // 图表是否为空数据

/** 原始数据：表单 */
const originQueryForm = {
  timer: [dayjs(), dayjs()],
  currMeter: "", // 当前选择的电表
  meterList: [], // 存放电表列表：下拉选项
}
/** 日历配置：禁选日期 */
const disabledDate = (date) => {
  return date > Date.now() || date < dayjs().subtract(6, 'month')
}

/** 表单：数据结构 */
const queryForm = reactive(cloneDeep(originQueryForm));


/** 图表：引用图表实例&DOM */
let linesChartObj = null;
const linesChartDom = ref(null);


/** 状态管理：电站ID */
const appStore = useAppStore();
const { stationId } = appStore.useStationState({ // 获取stationId
  stationIdWatch() { // 监听stationId的变化，触发该方法执行
    queryForm.meterList = [];
    updateMeterList(stationId.value)
  }
})




/** ====== [生命钩子] =================================================================== */
onMounted(() => {
  nextTick(() => {
    if (linesChartDom.value) {
      linesChartObj = echarts.init(linesChartDom.value, null, {
        renderer: 'canvas',
        devicePixelRatio: window.devicePixelRatio || 1,
      }); // 初始化图表，执行图表渲染
      observeChartResize();

      // 初始化一个空图表框架，确保图表容器显示
      const defaultChartData = {
        startTime: dayjs().startOf('day').format('YYYY-MM-DD 00:00:00'),
        endTime: dayjs().endOf('day').format('YYYY-MM-DD 23:59:59'),
        lines: [
          {
            key: 'p_total',
            name: $str.getKey('数据分析/有功功率'),
            data: [],
            options: {
              lineColor: '#1D77F5',
              areaStyle: {
                show: true,
                minColor: 'rgba(19, 58, 255, 0.04)',
                maxColor: 'rgba(9, 170, 251, 0.4)'
              },
              markLineStyle: {
                show: false,
                showMin: true,
                showMax: true,
              },
            }
          },
          {
            key: 'q_total',
            name: $str.getKey('数据分析/无功功率'),
            data: [],
            options: {
              lineColor: '#FFC859',
              areaStyle: {
                show: true,
                minColor: 'rgba(255, 200, 89, 0.04)',
                maxColor: 'rgba(255, 200, 89, 0.4)'
              },
              markLineStyle: {
                show: false,
                showMin: true,
                showMax: true,
              },
            }
          },
          {
            key: 'pf',
            name: $str.getKey('数据分析/功率因数'),
            data: [],
            options: {
              lineColor: '#00D4AA',
              areaStyle: {
                show: true,
                minColor: 'rgba(0, 212, 170, 0.04)',
                maxColor: 'rgba(0, 212, 170, 0.4)'
              },
              markLineStyle: {
                show: false,
                showMin: true,
                showMax: true,
              },
            }
          }
        ]
      };
      const option = getChartOption(defaultChartData);
      linesChartObj.setOption(option, true);
      // 确保图表正确调整大小
      linesChartObj.resize();
    }
  });
})




/** ====== [业务：功能方法] =================================================================== */

/** 时间范围发生变化 */
function timeRangeChange() {
  if (queryForm.timer && queryForm.timer.length === 2) {
    // 确保日期格式正确，转换为 dayjs 对象用于后续处理
    const start = dayjs(queryForm.timer[0]);
    const end = dayjs(queryForm.timer[1]);
    queryForm.timer = [start, end];
    if (queryForm.currMeter) {
      onSearch();
    }
  }
}


/** 选中的电表发生变化 */
function selectedMeterChange() { }
watch(() => queryForm.currMeter, (nv, ov) => {
  if (nv) {
    onSearch();
  }
})


/** 查询 */
function onSearch() {
  // 校验日期
  if (!queryForm.timer || queryForm.timer.length !== 2) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请选择日期范围')
    });
    return false;
  }
  if (!queryForm.currMeter) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请选择电表')
    });
    return false;
  }

  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
  debounceTimer.value = setTimeout(() => {
    updateChart();
  }, 150);
}

/** 重置 */
function onReset() {
  const originForm = cloneDeep(originQueryForm);
  queryForm.timer = originForm.timer;
  queryForm.currMeter = queryForm.meterList.length > 0 ? queryForm.meterList[0].value : "";
  onSearch();
}



/** ====== [辅助：功能方法] =================================================================== */

/** 更新电表的下拉选项&选中项 */
async function updateMeterList(stationId) {
  const list = await meterApi.getMeterList({ stationId: stationId });

  list.rows.forEach(item => {
    queryForm.meterList.push({
      label: item.meterName,
      value: item.uuid,
    });
  });

  queryForm.currMeter = queryForm.meterList.length > 0 ? queryForm.meterList[0].value : "";
}

/** 刷新图表 */
async function updateChart() {
  chartLoading.value = true;
  chartEmpty.value = false;

  const params = {
    stationId: stationId.value,
    uuid: queryForm.currMeter,
    startTime: dayjs(queryForm.timer[0]).format('YYYY-MM-DD 00:00:00'),
    endTime: dayjs(queryForm.timer[1]).format('YYYY-MM-DD 23:59:59'),
  }
  queryMeterAnalysisData(params).then(res => {
    const chartData = {
      startTime: params.startTime,
      endTime: params.endTime,
      lines: [
        {
          key: 'p_total',
          name: $str.getKey('数据分析/有功功率'),
          data: [],
          options: {
            lineColor: '#1D77F5',
            areaStyle: {
              show: true,
              minColor: 'rgba(19, 58, 255, 0.04)',
              maxColor: 'rgba(9, 170, 251, 0.4)'
            },
            markLineStyle: {
              show: false,
              showMin: true,
              showMax: true,
            },
          }
        },
        {
          key: 'q_total',
          name: $str.getKey('数据分析/无功功率'),
          data: [],
          options: {
            lineColor: '#FFC859',
            areaStyle: {
              show: true,
              minColor: 'rgba(255, 200, 89, 0.04)',
              maxColor: 'rgba(255, 200, 89, 0.4)'
            },
            markLineStyle: {
              show: false,
              showMin: true,
              showMax: true,
            },
          }
        },
        {
          key: 'pf',
          name: $str.getKey('数据分析/功率因数'),
          data: [],
          options: {
            lineColor: '#00D4AA',
            areaStyle: {
              show: true,
              minColor: 'rgba(0, 212, 170, 0.04)',
              maxColor: 'rgba(0, 212, 170, 0.4)'
            },
            markLineStyle: {
              show: false,
              showMin: true,
              showMax: true,
            },
          }
        }
      ]
    }

    // 处理接口返回的数据
    if (res.code == 200 && res.data && Array.isArray(res.data)) {
      const data = res.data;
      console.log({ data });

      chartData.lines.forEach(line => {
        line.data = data.map(item => [item.record_time, item[line.key]]);
        // if (data[line.key]) {
        //   line.data = data[line.key].map(item => [item.time, item.value]);
        // }
      });
      console.log(chartData)

      // 判断是否为空数据
      const hasData = chartData.lines.some(line => line.data && line.data.length > 0);
      chartEmpty.value = !hasData;

      // 渲染图表 - 即使没有数据也显示图表框架
      if (linesChartObj) {
        const option = getChartOption(chartData);
        linesChartObj.setOption(option, true);
        // 确保图表正确调整大小
        nextTick(() => {
          linesChartObj?.resize();
        });
      }
    } else {
      chartEmpty.value = true;
      // 即使没有数据也显示图表框架
      if (linesChartObj) {
        const chartData = {
          startTime: params.startTime,
          endTime: params.endTime,
          lines: [
            {
              key: 'p_total',
              name: $str.getKey('数据分析/有功功率'),
              data: [],
              options: {
                lineColor: '#1D77F5',
                areaStyle: {
                  show: true,
                  minColor: 'rgba(19, 58, 255, 0.04)',
                  maxColor: 'rgba(9, 170, 251, 0.4)'
                },
                markLineStyle: {
                  show: false,
                  showMin: true,
                  showMax: true,
                },
              }
            },
            {
              key: 'q_total',
              name: $str.getKey('数据分析/无功功率'),
              data: [],
              options: {
                lineColor: '#FFC859',
                areaStyle: {
                  show: true,
                  minColor: 'rgba(255, 200, 89, 0.04)',
                  maxColor: 'rgba(255, 200, 89, 0.4)'
                },
                markLineStyle: {
                  show: false,
                  showMin: true,
                  showMax: true,
                },
              }
            },
            {
              key: 'pf',
              name: $str.getKey('数据分析/功率因数'),
              data: [],
              options: {
                lineColor: '#00D4AA',
                areaStyle: {
                  show: true,
                  minColor: 'rgba(0, 212, 170, 0.04)',
                  maxColor: 'rgba(0, 212, 170, 0.4)'
                },
                markLineStyle: {
                  show: false,
                  showMin: true,
                  showMax: true,
                },
              }
            }
          ]
        };
        const option = getChartOption(chartData);
        linesChartObj.setOption(option, true);
      }
    }

    chartLoading.value = false;
  }).catch(err => {
    console.error('电表数据分析数据获取失败：', err);
    chartLoading.value = false;
    chartEmpty.value = true;
    // 即使出错也显示图表框架
    if (linesChartObj) {
      const chartData = {
        startTime: params.startTime,
        endTime: params.endTime,
        lines: [
          {
            key: 'p_total',
            name: $str.getKey('数据分析/有功功率'),
            data: [],
            options: {
              lineColor: '#1D77F5',
              areaStyle: {
                show: true,
                minColor: 'rgba(19, 58, 255, 0.04)',
                maxColor: 'rgba(9, 170, 251, 0.4)'
              },
              markLineStyle: {
                show: false,
                showMin: true,
                showMax: true,
              },
            }
          },
          {
            key: 'q_total',
            name: $str.getKey('数据分析/无功功率'),
            data: [],
            options: {
              lineColor: '#FFC859',
              areaStyle: {
                show: true,
                minColor: 'rgba(255, 200, 89, 0.04)',
                maxColor: 'rgba(255, 200, 89, 0.4)'
              },
              markLineStyle: {
                show: false,
                showMin: true,
                showMax: true,
              },
            }
          },
          {
            key: 'pf',
            name: $str.getKey('数据分析/功率因数'),
            data: [],
            options: {
              lineColor: '#00D4AA',
              areaStyle: {
                show: true,
                minColor: 'rgba(0, 212, 170, 0.04)',
                maxColor: 'rgba(0, 212, 170, 0.4)'
              },
              markLineStyle: {
                show: false,
                showMin: true,
                showMax: true,
              },
            }
          }
        ]
      };
      const option = getChartOption(chartData);
      linesChartObj.setOption(option, true);
    }
  });
}


/** 监听图表容器尺寸变化，重置图表尺寸
 * 容器由 flex 布局撑开，侧边栏折叠等场景不会触发 window 的 resize，
 * 只靠 window resize 会让 canvas 被 CSS 拉伸从而文字发虚
 */
let resizeObserver = null;
function observeChartResize() {
  if (!linesChartDom.value || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => {
    linesChartObj?.resize();
  });
  resizeObserver.observe(linesChartDom.value);
}

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  linesChartObj?.dispose();
  linesChartObj = null;
})


</script>
<style lang="scss" scoped>
.panel-container {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  --top-container-height: 70px;
  --bottom-container-height: 70px;

  .top-container {
    padding: 0 24px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 1);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;

    .power-form {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 16px;
    }

    .form-left-items {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .el-form-item {
      margin-bottom: 0;
      margin-right: 0;
      display: flex;
      align-items: center;
      height: 40px;

      :deep(.el-form-item__label) {
        font-size: 14px;
        color: rgba(46, 46, 46, 0.85);
        font-weight: 400;
        padding-right: 12px;
        line-height: 40px;
        height: 40px;
        display: flex;
        align-items: center;
      }

      :deep(.el-form-item__content) {
        line-height: 40px;
        display: flex;
        align-items: center;

        .el-range-editor {
          width: 260px !important;
          min-width: 260px !important;
          max-width: 260px !important;
        }
      }
    }

    .filter-date-picker {

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }

      :deep(.el-range-input) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }

      :deep(.el-range-separator) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }
    }

    .filter-select-input {
      width: 230px !important;
      min-width: 230px !important;
      max-width: 230px !important;

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      height: 40px;
    }
  }

  .main-container {
    box-sizing: border-box;
    flex: 1;
    width: 100%;
    padding: 20px;
    background: rgba(255, 255, 255, 1);
    border-radius: 0 0 20px 20px;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;

    .chart-container {
      width: 100%;
      flex: 1;
      min-height: 0;
    }

    .chart-status {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.9);
      z-index: 10;

      .status-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;

        .loading-text,
        .empty-text {
          font-family: "HarmonyOS Sans SC", sans-serif;
          font-size: 14px;
          color: rgba(46, 46, 46, 0.65);
          font-weight: 400;
        }
      }
    }
  }

  // 设置图表tooltip的样式
  ::v-deep .chart-tooltip {
    padding: 12px 16px !important;
    border-radius: 8px !important;
    background: #fff !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
    border: none !important;
  }

  ::v-deep .chart-tooltip__time {
    margin-bottom: 8px;
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #6A727C;
  }

  ::v-deep .chart-tooltip__items-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  ::v-deep .chart-tooltip__series {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 8px;
    border-radius: 6px;
  }

  ::v-deep .chart-tooltip__series-name,
  ::v-deep .chart-tooltip__value {
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    margin-left: 4px;
    color: var(--text-icon-font-gy-190-primary, rgba(0, 0, 0, 0.90));
  }

  ::v-deep .chart-tooltip__point {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 4px;
    flex-shrink: 0;
  }
}

.el-date-editor {
  .el-range__close-icon {
    display: none !important;
  }
}

// 选中日期时的颜色
.el-date-editor.el-input__wrapper:hover {
  box-shadow: none !important;
}
</style>
